import React, { useCallback, useEffect, useRef, useState } from "react";
import { auth, firebase } from "../../firebase/firebase.utils";
import { loadStripe } from "@stripe/stripe-js";
import "./TeamSubscription.scss";

const API = `https://us-central1-${firebase.app().options.projectId}.cloudfunctions.net/teamSubscriptionsApi`;
const finished = (team) => !team || ["expired", "canceled", "incomplete_expired"].includes(team.status);
const stripeClients = new Map();
// Serialize initialization/cleanup, including React Strict Mode and quick tab changes.
let checkoutTask = Promise.resolve();
function CheckoutForm({ session, onComplete }) {
  const container = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let disposed = false, checkout;
    setError("");
    checkoutTask = checkoutTask.then(async () => {
      if (disposed) return;
      let client = stripeClients.get(session.publishableKey);
      if (!client) {
        client = loadStripe(session.publishableKey).catch((error) => {
          stripeClients.delete(session.publishableKey);
          throw error;
        });
        stripeClients.set(session.publishableKey, client);
      }
      const stripe = await client;
      if (disposed) return;
      if (!stripe) throw new Error("Stripe is unavailable.");
      checkout = await stripe.initEmbeddedCheckout({ clientSecret: session.clientSecret,
        onComplete: () => { if (!disposed) onComplete(); } });
      if (disposed) { checkout.destroy(); checkout = null; }
      else checkout.mount(container.current);
    }).catch(() => {
      checkout?.destroy();
      checkout = null;
      if (!disposed) setError("Secure checkout could not load. Go back and choose Continue checkout to retry.");
    });
    return () => { disposed = true; checkout?.destroy(); };
  }, [session, onComplete]);
  return <div className="teamSubscription__checkout">
    {error && <p role="alert">{error}</p>}
    <div ref={container} />
  </div>;
}
const money = (quote) => {
  if (!quote) return "";
  const format = new Intl.NumberFormat(undefined, { style: "currency", currency: quote.currency });
  return format.format(quote.amount / 10 ** format.resolvedOptions().maximumFractionDigits);
};

async function request(action, values = {}) {
  if (!auth.currentUser) throw new Error("Sign in to manage team subscriptions.");
  const response = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await auth.currentUser.getIdToken()}` },
    body: JSON.stringify({ action, ...values }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "The team service is temporarily unavailable.");
  return data;
}

export default function TeamSubscription({ currentUser }) {
  const [state, setState] = useState(null);
  const [plan, setPlan] = useState("pro");
  const [period, setPeriod] = useState("monthly");
  const [seats, setSeats] = useState(5);
  const [email, setEmail] = useState("");
  const [pricing, setPricing] = useState(null);
  const priceCache = useRef(new Map());
  const [priceRevision, setPriceRevision] = useState(0);
  const [showPurchase, setShowPurchase] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [inviteURL, setInviteURL] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const team = state?.own;
  const managing = team && !finished(team) && team.status !== "checkout";
  const pendingCheckout = team?.status === "checkout";
  const active = team?.accessUntil > Date.now();
  const selectedPlan = managing || pendingCheckout ? team.plan : plan;
  const selectedPeriod = managing || pendingCheckout ? team.period : period;
  const members = team?.members || [];
  const occupied = members.filter((m) => ["active", "invited"].includes(m.state)).length;
  const hasInvitations = !!state?.invitations?.length;
  const showSales = !state?.sponsored && (managing || pendingCheckout || !hasInvitations || showPurchase);
  const priceKey = `${currentUser?.uid}:${selectedPlan}:${selectedPeriod}:${managing ? team.id : "new"}`;
  const validSeats = Number.isInteger(seats) && seats >= 2 && seats <= state?.maxSeats;
  const amount = pricing?.key === priceKey && validSeats ? pricing.totals?.[seats] : null;
  const quote = Number.isFinite(amount) ? { amount, currency: pricing.currency } : null;

  const refresh = useCallback(async () => {
    const next = await request("state");
    setState(next);
    if (next.own?.status !== "checkout") setCheckout(null);
    window.dispatchEvent(new Event("antlogy:membership-refresh"));
    return next;
  }, []);

  useEffect(() => {
    let live = true;
    setState(null);
    setError("");
    setShowPurchase(false);
    setCheckout(null);
    setConfirming(false);
    priceCache.current.clear();
    if (currentUser?.uid) {
      request("state").then((next) => {
        if (!live) return;
        setState(next);
        if (next.own && !finished(next.own)) setSeats(next.own.seats);
        window.dispatchEvent(new Event("antlogy:membership-refresh"));
      }).catch((e) => { if (live) setError(e.message); });
    }
    return () => { live = false; };
  }, [currentUser?.uid]);

  const checkoutComplete = useCallback(() => {
    setCheckout(null);
    setMessage("Checkout complete. Confirming your subscription…");
    setConfirming(true);
  }, []);

  useEffect(() => {
    if (!confirming) return;
    let stopped = false, timer, attempts = 0;
    async function check() {
      try {
        const next = await refresh();
        if (stopped) return;
        if (next.own?.accessUntil > Date.now()) {
          setConfirming(false); setMessage("Your team subscription is active."); return;
        }
      } catch { /* A webhook may hold the team lock; retry briefly. */ }
      if (stopped) return;
      if (++attempts < 6) timer = setTimeout(check, 3000);
      else {
        setConfirming(false);
        setMessage("Confirmation is taking longer than usual. Press Refresh shortly; your saved checkout will be checked.");
      }
    }
    check();
    return () => { stopped = true; clearTimeout(timer); };
  }, [confirming, refresh]);

  useEffect(() => {
    if (!checkout) return;
    const timer = setTimeout(() => {
      setCheckout(null);
      setMessage("This checkout has reached its time limit. Press Refresh to check its status before starting again.");
    }, Math.max(0, checkout.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [checkout]);

  useEffect(() => {
    let live = true;
    setQuoteError("");
    if (!state || !showSales || pendingCheckout) return;
    const cached = priceCache.current.get(priceKey);
    if (cached) { setPricing(cached); return; }
    // One server-priced table covers every allowed quantity; typing never fetches.
    request("quote", { plan: selectedPlan, period: selectedPeriod, seats: 2 })
      .then((next) => {
        const table = { ...next, key: priceKey };
        priceCache.current.set(priceKey, table);
        if (live) setPricing(table);
      })
      .catch((e) => { if (live) setQuoteError(e.message); });
    return () => { live = false; };
  }, [state?.maxSeats, showSales, pendingCheckout, priceKey, selectedPlan, selectedPeriod, priceRevision]);

  async function act(action, values, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setError(""); setMessage(""); setInviteURL("");
    try {
      const result = await request(action, action === "checkout" ? { ...values, embedded: true } : values);
      if (result.clientSecret) {
        setCheckout(result);
        await refresh();
        return;
      }
      if (result.url) { window.location.assign(result.url); return; }
      if (action === "cancelCheckout") { setCheckout(null); setConfirming(false); }
      setMessage(result.message || "Saved.");
      setInviteURL(result.inviteURL || "");
      if (action === "invite") setEmail("");
      if (["accept", "reject", "leave"].includes(action)) setShowPurchase(false);
      await refresh();
    } catch (e) {
      // A failed response can still leave a saved session. Offer Resume/Discard.
      if (["checkout", "cancelCheckout"].includes(action)) { try { await refresh(); } catch {} }
      setError(e.message);
    }
    finally { setBusy(false); }
  }

  async function reload() {
    setBusy(true); setError("");
    try {
      await auth.currentUser?.reload();
      const next = await refresh();
      if (next.own && !finished(next.own)) setSeats(next.own.seats);
      priceCache.current.clear();
      setPriceRevision((value) => value + 1);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!currentUser?.uid) return <section className="teamSubscription"><h2>Team Subscription</h2><p>Sign in using your existing Antlogy account to continue.</p></section>;

  return (
    <section className="teamSubscription" aria-busy={busy}>
      <header>
        <div><h2>Team Subscription</h2><p>{state?.sponsored ? "Your team seat and membership." : hasInvitations && !showSales ? "Accept an invitation to activate your seat." : "One bill. A seat for each person."}</p></div>
        <button type="button" onClick={reload} disabled={busy}>Refresh</button>
      </header>
      {error && <p role="alert" className="teamSubscription__notice">{error}</p>}
      {message && <p role="status" className="teamSubscription__notice">{message}</p>}
      {inviteURL && <p>Invitation link: <a href={inviteURL}>{inviteURL}</a></p>}
      {!state && !error && <p role="status">Loading your subscription…</p>}

      {state?.invitations?.map((invitation) => (
        <div className="teamSubscription__card" key={invitation.id}>
          <strong>Invitation from {invitation.ownerEmail}</strong>
          <p>They are offering you an Ant {invitation.plan === "business" ? "Business" : "Pro"} seat.</p>
          <button type="button" disabled={busy || !!state.sponsored || !finished(team)}
            onClick={() => act("accept", { teamId: invitation.id }, "Accept this seat? Any personal subscription you already pay for will continue until you cancel it separately.")}>
            Accept team seat
          </button>
          <button type="button" disabled={busy} onClick={() => act("reject", { teamId: invitation.id },
            "Reject this invitation? The owner will see your response and the reserved seat will be freed. You will need a new invitation to accept later.")}>
            Reject team seat
          </button>
        </div>
      ))}

      {state?.sponsored ? (
        <div className="teamSubscription__card">
          <h3>Your team seat</h3>
          <p>Ant {state.sponsored.plan === "business" ? "Business" : "Pro"} · {state.sponsored.leaving ? "Leaving team" : state.sponsored.accessUntil > Date.now() ? "Active" : "Awaiting team billing"}</p>
          <p>{state.sponsored.leaving ? "You have left the team. Access removal is pending and will retry automatically." : "Your team owner manages billing. You can leave this team whenever you choose."}</p>
          <button type="button" disabled={busy} onClick={() => act("leave", {}, state.sponsored.leaving ? null :
            "Leave this team and give up its subscription benefits? Your personal subscriptions will remain unchanged. To get a seat again in the future, contact the person who invited you so they can reserve a new seat for you.")}>
            {state.sponsored.leaving ? "Retry access removal" : "Leave team"}
          </button>
        </div>
      ) : state && showSales && (
        <>
          {managing && <div className="teamSubscription__card">
            <strong>Ant {team.plan === "business" ? "Business" : "Pro"} · {team.seats} seats</strong>
            <p>{occupied} assigned or reserved · {Math.max(0, team.seats - occupied)} available</p>
            <p>{active ? "Access paid through " + new Date(team.accessUntil).toLocaleDateString() : "Access is awaiting payment or a billing review."}
              {team.cancelAtPeriodEnd && " · Renewal is canceled."}</p>
            {team.syncError && <p role="status">{team.syncError}</p>}
            {team.overCapacity && <p role="alert">The seat count was reduced below your assignments. Contact support.</p>}
            {team.paymentHold && <p role="alert">A refund or dispute requires a billing review.</p>}
            <button type="button" disabled={busy} onClick={() => act("portal")}>Manage billing & invoices</button>
            {team.pendingPaymentURL && <a href={team.pendingPaymentURL}>Complete pending payment</a>}
          </div>}

          {checkout ? <div>
            <div className="teamSubscription__checkoutActions">
              <button type="button" disabled={busy} onClick={() => setCheckout(null)}>Back to team</button>
              <button type="button" disabled={busy} onClick={() => act("cancelCheckout")}>Discard checkout</button>
            </div>
            <CheckoutForm key={checkout.checkoutId} session={checkout} onComplete={checkoutComplete} />
          </div> : confirming ? <p role="status">Checking payment and activating your team…</p> : pendingCheckout ? <div className="teamSubscription__card">
            <p>Your checkout is open. Continue with the saved selection, or discard it to choose again.</p>
            <button type="button" disabled={busy} onClick={() => act("checkout", { plan: team.plan, period: team.period, seats: team.seats })}>Continue checkout</button>
            <button type="button" disabled={busy} onClick={() => act("cancelCheckout")}>Discard checkout</button>
          </div> : <form onSubmit={(e) => {
            e.preventDefault();
            act(managing ? "seats" : "checkout", { plan: selectedPlan, period: selectedPeriod, seats }, managing
              ? seats < team.seats
                ? "Reduce seats now? There is no refund or credit for this period. The new recurring price applies at renewal."
                : "Add seats? Stripe will charge the prorated difference now. Additional seats activate after payment succeeds."
              : "Continue to secure checkout? Your own seat is included. Any existing personal subscription continues until you cancel it separately.");
          }}>
            <fieldset disabled={busy}>
              {!managing && <div className="teamSubscription__fields">
                <label>Plan<select value={plan} onChange={(e) => setPlan(e.target.value)}><option value="pro">Ant Pro</option><option value="business">Business</option></select></label>
                <label>Billing<select value={period} onChange={(e) => setPeriod(e.target.value)}><option value="monthly">Monthly</option><option value="annual">Yearly</option></select></label>
              </div>}
              <label>Total seats, including you
                <span className="teamSubscription__seatRow">
                  <input className="teamSubscription__seats" type="number" inputMode="numeric" min={Math.max(2, managing ? occupied : 2)} max={state.maxSeats} step="1" value={seats} onChange={(e) => setSeats(e.target.value === "" ? "" : Number(e.target.value))} required />
                  {quote && <small className="teamSubscription__perSeat" aria-live="polite">{money({ ...quote, amount: quote.amount / seats })} / seat / {selectedPeriod === "annual" ? "year" : "month"} on average</small>}
                </span>
              </label>
              <p className="teamSubscription__price" aria-live="polite">{quote ? <>{money(quote)} / {selectedPeriod === "annual" ? "year" : "month"} <small>before tax</small></> : <small>{quoteError ? "Price unavailable" : validSeats ? "Loading price…" : `Enter 2–${state.maxSeats} seats`}</small>}</p>
              {quoteError && <p role="alert">{quoteError}</p>}
              <p className="teamSubscription__hint">Any configured quantity discount is included. Pending invitations reserve a seat.</p>
              {managing && <p className="teamSubscription__hint">Added seats are prorated now. Removed seats receive no credit. This amount is your recurring total, not a proration quote.</p>}
              <button type="submit" disabled={!quote || (managing && (!active || team.pendingUpdate || seats === team.seats || team.cancelAtPeriodEnd))}>
                {managing ? "Update seat count" : "Continue to secure checkout"}
              </button>
            </fieldset>
          </form>}

          {managing && <>
            <h3>People</h3>
            <form onSubmit={(e) => { e.preventDefault(); act("invite", { email }); }}>
              <fieldset disabled={busy || !active || occupied >= team.seats} className="teamSubscription__invite">
                <label>Email address<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@example.com" required /></label>
                <button type="submit">Invite</button>
              </fieldset>
            </form>
            <ul className="teamSubscription__members">
              {members.map((member) => <li key={member.id}>
                <div><strong>{member.email}</strong><small>{member.owner ? "You · team owner" : member.state === "rejected" ? "Invitation rejected" : member.state === "invited" ? (member.sent ? "Invitation sent" : "Invitation reserved · email not sent") : member.state === "removing" ? "Removing access…" : "Seat assigned"}</small></div>
                <div>
                  {member.state === "invited" && <button type="button" disabled={busy || !active} onClick={() => act("invite", { email: member.email })}>Resend</button>}
                  {member.state === "rejected" && <button type="button" disabled={busy || !active || occupied >= team.seats} onClick={() => act("invite", { email: member.email })}>Invite again</button>}
                  {!member.owner && <button type="button" disabled={busy} onClick={() => act("remove", { memberId: member.id }, `Remove ${member.email}? Their team access will end; your billed seat count stays the same.`)}>Remove</button>}
                </div>
              </li>)}
            </ul>
            <p className="teamSubscription__hint">New members use the usual signup and email verification, then accept here. Removing a person frees a seat; use Update seat count to change billing.</p>
          </>}
        </>
      )}
      {state && !state.sponsored && hasInvitations && finished(team) && (
        <button type="button" className="teamSubscription__purchaseToggle" aria-expanded={showPurchase} disabled={busy} onClick={() => setShowPurchase((value) => !value)}>
          {showPurchase ? "Hide purchase options" : "Would you like to purchase your own team subscription instead?"}
        </button>
      )}
    </section>
  );
}
